import { useEffect, useState } from "react";
import { api } from "../lib/api";

/**
 * Green-pulse dot when a user is online (last_seen ≤ 5 min ago),
 * amber dot when idle (5-30 min), nothing when offline.
 *
 * Usage: <PresenceDot userId={id} /> or pass a preloaded status map.
 * If `statusMap` is provided, no fetch happens — just looks up.
 */
export default function PresenceDot({ userId, statusMap, className = "" }) {
  const [status, setStatus] = useState(statusMap ? (statusMap[userId] || "offline") : null);

  useEffect(() => {
    if (statusMap) {
      setStatus(statusMap[userId] || "offline");
      return;
    }
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/presence/lookup", { user_ids: [userId] });
        if (!cancelled) setStatus(data?.items?.[0]?.status || "offline");
      } catch { if (!cancelled) setStatus("offline"); }
    })();
    return () => { cancelled = true; };
  }, [userId, statusMap]);

  if (!status || status === "offline") return null;
  const color = status === "online" ? "bg-emerald-500" : "bg-slate-400";
  const ping = status === "online" ? "bg-emerald-400" : "bg-slate-300";
  return (
    <span className={`relative inline-flex h-2 w-2 shrink-0 ${className}`} title={status === "online" ? "Online now" : "Idle"} data-testid={`presence-dot-${userId}`}>
      {status === "online" && (
        <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-60 ${ping}`} />
      )}
      <span className={`relative inline-flex rounded-full h-2 w-2 ${color}`} />
    </span>
  );
}
